import React, { forwardRef, useState } from 'react'

import * as S from './styles'
import { getInputType } from './utils'

import { ReactComponent as EyeOffIcon } from '../../../../assets/svgs/eye-off.svg'
import { ReactComponent as EyeIcon } from '../../../../assets/svgs/eye.svg'
import { generateId, preventWheelInput } from '../../../lib/helpers'
import { FormItem } from '../FormItem'
import { IconGenerator } from '../IconGenerator'

import type { TextInputProps } from './types'
/**
 * Form input component
 * @component
 * @param {object} error - error object {type: 'required', message: 'field is required'}
 * @param {string} label - text label above input
 * @param {string} placeholder - placeholder text
 * @param {function} onBlur - onBlur handler
 * @param {function} onClickPostfix - if postfix is clickable, what to do
 * @param {ReactNode} postfix - text or icon on the right side of the input
 * @param {boolean} hidden - hide input dom element (display:none)
 * @param {string} step - step attribute for number type input
 * @param {boolean} hideStepArrows - hides step arrows for number type input
 * @example
 * import { useForm } from 'react-hook-form'
 * const { register, formState: { errors } } = useForm()
 *
 * return (
 *   <Input {...register('name', { required: 'this field is required' })}
 *              label='Name'
 *              error={errors.name}
 *              placeholder="Type your name..."
 *   />
 * )
 */

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      error,
      label,
      name,
      postfix,
      hidden,
      readOnly,
      type,
      onClickPostfix,
      shouldKeepLabelSpacing,
      hint,
      noSpins,
      hideStepArrows,
      step = '0.01',
      'data-form-item-testid': dataFormItemTestId,
      tooltip,
      ...restProps
    },
    ref
  ) => {
    const id = generateId(5)
    const [isPasswordVisible, setIsPasswordVisible] = useState(false)

    return (
      <FormItem
        label={label}
        shouldKeepLabelSpacing={shouldKeepLabelSpacing}
        error={error}
        hidden={hidden}
        hint={hint}
        data-testid={dataFormItemTestId}
        htmlFor={id}
        tooltip={tooltip}
      >
        <S.InputWrapper>
          <S.TextInput
            ref={ref}
            id={id}
            name={name}
            $error={error}
            $postfix={postfix}
            $noSpins={noSpins}
            $hideStepArrows={hideStepArrows}
            readOnly={readOnly}
            type={getInputType(type, isPasswordVisible)}
            step={type === 'number' ? step : undefined}
            onWheel={type === 'number' ? preventWheelInput : undefined}
            {...restProps}
          />
          {type === 'password' && (
            <S.PasswordIcon
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            >
              <IconGenerator
                iconComponent={isPasswordVisible ? EyeIcon : EyeOffIcon}
                size={24}
              />
            </S.PasswordIcon>
          )}
          {postfix && (
            <S.Postfix
              onClick={onClickPostfix}
              isClickable={!!onClickPostfix}
              $readOnly={readOnly}
            >
              {postfix}
            </S.Postfix>
          )}
        </S.InputWrapper>
      </FormItem>
    )
  }
)

TextInput.displayName = 'TextInput'
